import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

// icons
import { Edit, Pause, Play, ArrowLeft, Key } from 'iconsax-react';

// project imports
import { getTenant, updateTenant, suspendTenant, reactivateTenant, getTenantUsers, resetTenantUserPassword } from '../../services/tenantApi';
import { useToast } from '../../components/ui/use-toast';
import { Input } from '../../components/ui/input';

// ==============================|| TENANT DETAILS ||============================== //

const TenantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("contact");
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionDuration, setSuspensionDuration] = useState('30');
  const [resetDialog, setResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', domain: '', contactEmail: '', contactPhone: '' });
  const [editLoading, setEditLoading] = useState(false);

  const fetchTenant = useCallback(async () => {
    if (!id || id === 'undefined') {
      setTenant(null);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await getTenant(id);
      setTenant(response.data);
    } catch (error) {
      console.error('Error fetching tenant:', error);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const fetchTenantUsers = useCallback(async () => {
    if (!id || id === 'undefined') return;
    
    try {
      setUsersLoading(true);
      const response = await getTenantUsers(id);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch users. Please try again later.',
        variant: 'destructive'
      });
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [id, toast]);

  // Fetch users when Users tab is selected
  useEffect(() => {
    if (activeTab === "users" && tenant && users.length === 0) {
      fetchTenantUsers();
    }
  }, [activeTab, tenant, users.length, fetchTenantUsers]);

  const handleResetPassword = async () => {
    if (!userToReset) return;
    try {
      setResetLoading(true);
      const result = await resetTenantUserPassword(id, userToReset._id || userToReset.id);
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Password reset successfully!'
        });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to reset password.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to reset password. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setResetLoading(false);
      setResetDialog(false);
      setUserToReset(null);
    }
  };

  const handleSuspend = async () => {
    try {
      await suspendTenant(id, {
        reason: suspensionReason,
        duration: suspensionDuration
      });
      setSuspendDialog(false);
      fetchTenant();
    } catch (error) {
      console.error('Error suspending tenant:', error);
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateTenant(id);
      fetchTenant();
    } catch (error) {
      console.error('Error reactivating tenant:', error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      await updateTenant(id, {
        name: editFormData.name,
        domain: editFormData.domain,
        contact: {
          email: editFormData.contactEmail,
          phone: editFormData.contactPhone
        }
      });
      setEditDialog(false);
      fetchTenant(); // refresh
      toast({ title: 'Success', description: 'Agency updated successfully.' });
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update agency', variant: 'destructive' });
    } finally {
      setEditLoading(false);
    }
  };

  const openEditDialog = () => {
    setEditFormData({
      name: tenant.name || '',
      domain: tenant.domain || '',
      contactEmail: tenant.contact?.email || '',
      contactPhone: tenant.contact?.phone || ''
    });
    setEditDialog(true);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none';
      case 'suspended': return 'bg-rose-100 text-rose-700 hover:bg-rose-100 border-none';
      case 'trial': return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-none';
      default: return 'bg-slate-100 text-slate-700 hover:bg-slate-100 border-none';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading...</div>;
  }

  if (!tenant) {
    return <div className="p-6 text-center text-muted-foreground">Tenant not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Back Button & Header section */}
      <div className="flex flex-col gap-1.5">
        <button
          onClick={() => navigate('/tenants')}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-fit focus:outline-none"
        >
          <ArrowLeft size={16} />
          Back to Agencies
        </button>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {tenant.name}
          </h1>
        <div className="flex gap-3">
          {tenant.status === 'active' ? (
            <Button
              variant="outline"
              className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-1.5"
              onClick={() => setSuspendDialog(true)}
            >
              <Pause size={18} />
              Suspend
            </Button>
          ) : (
            <Button
              variant="outline"
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-1.5"
              onClick={handleReactivate}
            >
              <Play size={18} />
              Reactivate
            </Button>
          )}
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
            onClick={openEditDialog}
          >
            <Edit size={18} />
            Edit
          </Button>
        </div>
      </div>
    </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left Side: Org Info */}
        <div className="md:col-span-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground border-b pb-2">
                Organization Info
              </h2>
              
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Status</p>
                <div>
                  <Badge className={`${getStatusBadgeClass(tenant.status)} font-semibold px-2.5 py-0.5`}>
                    {tenant.status}
                  </Badge>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Domain</p>
                <p className="text-sm text-foreground font-medium">{tenant.domain}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Code</p>
                <p className="text-sm text-foreground font-medium">{tenant.code}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Created Date</p>
                <p className="text-sm text-foreground font-medium">
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side: Tabbed Details */}
        <div className="md:col-span-8">
          <Card className="h-full">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full">
              <div className="border-b px-4 py-2">
                <TabsList className="bg-muted/50">
                  <TabsTrigger value="contact">Contact Info</TabsTrigger>
                  <TabsTrigger value="users">Users</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
              </div>
              
              <CardContent className="p-6 flex-1">
                <TabsContent value="contact" className="space-y-4 mt-0">
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Email</p>
                      <p className="text-sm text-foreground font-medium">{tenant.contact?.email}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Phone</p>
                      <p className="text-sm text-foreground font-medium">{tenant.contact?.phone}</p>
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Address</p>
                      <p className="text-sm text-foreground font-medium">{tenant.contact?.address?.street}</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="users" className="space-y-4 mt-0">
                  <h3 className="text-lg font-semibold text-foreground">
                    Users ({users.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold">Email</TableHead>
                          <TableHead className="font-semibold">Role</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usersLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                              Loading users...
                            </TableCell>
                          </TableRow>
                        ) : users.length > 0 ? (
                          users.map((user) => (
                            <TableRow key={user.id || user._id} className="hover:bg-muted/50 transition-colors">
                              <TableCell className="font-medium text-foreground">{user.name}</TableCell>
                              <TableCell className="text-muted-foreground">{user.email}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-2 py-0.5">
                                  {user.role}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={user.status === 'Active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none' : 'bg-slate-100 text-slate-700 hover:bg-slate-100 border-none'}>
                                  {user.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 focus:outline-none"
                                  title="Reset Password"
                                  onClick={() => {
                                    setUserToReset(user);
                                    setResetDialog(true);
                                  }}
                                >
                                  <Key size={18} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                              No users found for this agency
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4 mt-0">
                  <h3 className="text-lg font-semibold text-foreground">
                    Settings
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Tenant configuration settings will be displayed here.
                  </p>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Suspend Agency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase">Reason for Suspension</label>
              <Textarea
                placeholder="Describe reason for suspension..."
                rows={3}
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase">Duration</label>
              <Select value={suspensionDuration} onValueChange={setSuspensionDuration}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="indefinite">Indefinite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setSuspendDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSuspend}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!suspensionReason}
            >
              Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to reset the password for <strong className="text-foreground">{userToReset?.name}</strong> ({userToReset?.email})?
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              A temporary password will be generated, saved, and emailed to the user.
            </p>
          </div>
          <DialogFooter className="flex sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setResetDialog(false)} disabled={resetLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1.5"
              disabled={resetLoading}
            >
              {resetLoading ? 'Resetting...' : 'Confirm Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Agency Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4 py-2 text-left">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Agency Name</label>
                <Input 
                  value={editFormData.name} 
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Domain</label>
                <Input 
                  value={editFormData.domain} 
                  onChange={(e) => setEditFormData({...editFormData, domain: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Contact Email</label>
                <Input 
                  type="email"
                  value={editFormData.contactEmail} 
                  onChange={(e) => setEditFormData({...editFormData, contactEmail: e.target.value})} 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Contact Phone</label>
                <Input 
                  value={editFormData.contactPhone} 
                  onChange={(e) => setEditFormData({...editFormData, contactPhone: e.target.value})} 
                />
              </div>
            </div>
            <DialogFooter className="flex sm:justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)} disabled={editLoading}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetails;